import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormLabel,
  Paper,
  Radio,
  RadioGroup,
  TextField,
  Typography
} from '@material-ui/core'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import React from 'react'
import { useParams } from 'react-router-dom'

import { fetchJoiningDetailsForm, submitJoiningDetails } from 'api/newJoiners'

/**
 * THE NEW JOINER'S JOINING-DETAILS FORM (user, 2026-10-29). Replaces HR's
 * "Employee information" Google Form. No login: the token in the URL (from
 * the welcome email) is the authorisation.
 *
 * The questions come from the server (bella_details.QUESTIONS — HR's form
 * word for word, plus the ID card's intro and bio), so this page only lays
 * them out. On submit Bella adds the joiner to their centre's team roster,
 * draws their ID card and emails it with their logins. Once sent, the page
 * only says thank you: Aadhaar, PAN and bank numbers are never shown back.
 */
const errorDetail = (e) =>
  e?.response?.data?.detail || 'Something went wrong. Please try again.'

const FILE_KINDS = ['file', 'photo']

const Question = ({ q, value, file, onValue, onFile, maxMb }) => {
  const required = <span style={{ color: '#c62828' }}> *</span>
  if (q.kind === 'choice') {
    return (
      <FormControl component="fieldset" style={{ marginTop: 16 }}>
        <FormLabel component="legend" style={{ color: '#212121' }}>
          {q.label}
          {required}
        </FormLabel>
        <RadioGroup
          value={value || ''}
          onChange={(e) => onValue(e.target.value)}
        >
          {q.options.map((o) => (
            <FormControlLabel
              key={o}
              value={o}
              control={<Radio color="primary" size="small" />}
              label={o}
            />
          ))}
        </RadioGroup>
      </FormControl>
    )
  }
  if (FILE_KINDS.includes(q.kind)) {
    const photo = q.kind === 'photo'
    return (
      <Box mt={2}>
        <Typography variant="body2">
          {q.label}
          {required}
        </Typography>
        <Typography variant="caption" color="textSecondary" component="p">
          {photo
            ? `A clear photo of your face, like a passport photo (JPG or PNG, up to ${maxMb} MB).`
            : `A photo or PDF, up to ${maxMb} MB.`}
        </Typography>
        <Button
          variant="outlined"
          component="label"
          size="small"
          style={{ marginTop: 4, textTransform: 'none' }}
        >
          {file ? 'Change file' : 'Add file'}
          <input
            hidden
            type="file"
            accept={photo ? 'image/*' : 'image/*,application/pdf'}
            onChange={(e) => onFile(e.target.files[0] || null)}
          />
        </Button>
        {file && (
          <Typography variant="caption" style={{ marginLeft: 8 }}>
            {file.name}
          </Typography>
        )}
      </Box>
    )
  }
  return (
    <TextField
      label={
        <>
          {q.label}
          {required}
        </>
      }
      value={value || ''}
      onChange={(e) => onValue(e.target.value)}
      type={q.kind === 'date' ? 'date' : 'text'}
      InputLabelProps={q.kind === 'date' ? { shrink: true } : undefined}
      multiline={q.kind === 'long'}
      minRows={q.kind === 'long' ? 2 : undefined}
      inputProps={q.max ? { maxLength: q.max } : undefined}
      helperText={q.max ? `${(value || '').length}/${q.max}` : undefined}
      fullWidth
      margin="dense"
    />
  )
}

const JoiningDetails = () => {
  const { token } = useParams()
  const [info, setInfo] = React.useState(null)
  const [loadError, setLoadError] = React.useState('')
  const [values, setValues] = React.useState({})
  const [files, setFiles] = React.useState({})
  const [error, setError] = React.useState('')
  const [sending, setSending] = React.useState(false)

  React.useEffect(() => {
    fetchJoiningDetailsForm(token)
      .then((data) => {
        setInfo(data)
        setValues(data.prefill || {})
      })
      .catch((e) => setLoadError(errorDetail(e)))
  }, [token])

  const questions = React.useMemo(
    () => (info?.form?.sections || []).flatMap((s) => s.questions),
    [info]
  )

  const submit = async () => {
    const missing = questions.find((q) =>
      FILE_KINDS.includes(q.kind)
        ? !files[q.key]
        : !String(values[q.key] || '').trim()
    )
    if (missing) {
      setError(`${missing.label}: this is required.`)
      return
    }
    const body = new FormData()
    questions.forEach((q) => {
      if (FILE_KINDS.includes(q.kind)) body.append(q.key, files[q.key])
      else body.append(q.key, String(values[q.key]).trim())
    })
    setSending(true)
    setError('')
    try {
      setInfo(await submitJoiningDetails(token, body))
      window.scrollTo(0, 0)
    } catch (e) {
      setError(errorDetail(e))
    } finally {
      setSending(false)
    }
  }

  let content
  if (!info && !loadError) {
    content = (
      <Box textAlign="center" py={4}>
        <CircularProgress />
      </Box>
    )
  } else if (!info) {
    content = <Typography color="error">{loadError}</Typography>
  } else if (info.submitted) {
    content = (
      <Box display="flex" alignItems="flex-start" style={{ gap: 10 }}>
        <CheckCircleIcon style={{ color: '#2e7d32', fontSize: 32 }} />
        <Box>
          <Typography variant="h6">
            Thank you, {info.first_name}. We have your details.
          </Typography>
          <Typography paragraph>
            We&apos;ll email you your Openhouse ID card and your logins shortly.
            If anything needs to change, please reply to that email.
          </Typography>
        </Box>
      </Box>
    )
  } else if (info.closed) {
    content = <Typography>This link has expired. Please contact HR.</Typography>
  } else {
    const maxMb = info.form.max_file_mb
    content = (
      <>
        <Typography variant="h6" gutterBottom>
          Welcome to Openhouse, {info.first_name}!
        </Typography>
        <Typography variant="body2" color="textSecondary" paragraph>
          {info.designation} · {info.centre} · joined {info.joining_date}
        </Typography>
        <Typography paragraph>
          Please fill in your details below. We use them to set up your HR
          account (Keka: salary slips, leave and more) and to make your
          Openhouse ID card. Everything marked * is required. Your documents are
          seen only by our HR team.
        </Typography>
        {info.form.sections.map((section) => (
          <Box key={section.title} mt={3}>
            <Typography
              variant="subtitle1"
              style={{ fontWeight: 700, borderBottom: '1px solid #eee' }}
            >
              {section.title}
            </Typography>
            {section.questions.map((q) => (
              <React.Fragment key={q.key}>
                <Question
                  q={q}
                  value={values[q.key]}
                  file={files[q.key]}
                  maxMb={maxMb}
                  onValue={(v) => {
                    setValues((s) => ({ ...s, [q.key]: v }))
                    setError('')
                  }}
                  onFile={(f) => {
                    setFiles((s) => ({ ...s, [q.key]: f }))
                    setError('')
                  }}
                />
                {q.key === 'uan' && (
                  <Box
                    mt={1}
                    p={1.5}
                    style={{ background: '#f7f7f7', borderRadius: 4 }}
                  >
                    {info.form.uan_steps.map((line, i) => (
                      <Typography key={line} variant="body2" paragraph>
                        {i > 0 && i < info.form.uan_steps.length - 1
                          ? `${i}. `
                          : ''}
                        {line}
                      </Typography>
                    ))}
                  </Box>
                )}
              </React.Fragment>
            ))}
          </Box>
        ))}
        {error && (
          <Typography color="error" variant="body2" style={{ marginTop: 16 }}>
            {error}
          </Typography>
        )}
        <Box mt={3}>
          <Button
            color="primary"
            variant="contained"
            onClick={submit}
            disabled={sending}
          >
            {sending ? 'Sending…' : 'Submit'}
          </Button>
        </Box>
      </>
    )
  }

  return (
    <Box
      minHeight="100vh"
      display="flex"
      alignItems="flex-start"
      justifyContent="center"
      p={2}
      style={{ background: '#f5f5f5' }}
    >
      <Paper
        style={{ padding: 24, maxWidth: 720, width: '100%', marginTop: 32 }}
      >
        {content}
      </Paper>
    </Box>
  )
}

export default JoiningDetails
